<template>
  <div class="report-list">
    <TitleBar>
      <template v-slot:title>
        <translate>Reports</translate>
      </template>
    </TitleBar>

    <div class="content">
      <section v-for="section in orderedSections" :key="section.name">
        <h3>
          {{ sectionLabels[section.name] }}
        </h3>
        <div class="report-cards-wrap">
          <ReportLinkCard v-for="report in section.reports" :key="report.title" :report-data="report" />
        </div>
      </section>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import TitleBar from '@/components/TitleBar'
import ReportLinkCard from '@/components/reports/ReportLinkCard'

export default {
  name: 'Reports',
  components: { TitleBar, ReportLinkCard },
  data() {
    return {
      visibleReports: [],
      sectionOrder: ['free', 'vat', 'financial', 'insight'],
      sectionLabels: {},
      sectionedReports: {},
    }
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
      currentCompany: 'main/getCurrentCompany',
    }),
    currentYear() {
      const date = new Date()
      return date.getFullYear()
    },
    orderedSections() {
      return this.sectionOrder
        .map((section) => ({
          name: section,
          reports: this.sectionedReports[section] || [],
        }))
        .filter((section) => section.reports.length > 0)
    },
  },
  created() {
    this.setupData()
  },
  methods: {
    setupData() {
      this.sectionLabels = {
        free: this.$gettext('Basic'),
        vat: this.$gettext('VAT Reports'),
        financial: this.$gettext('Financial'),
        insight: this.$gettext('Insights'),
      }
      const allReports = [
        {
          section: 'free',
          to: { name: 'RevenueExpenses' },
          icon: 'fa-bar-chart',
          title: this.$gettext('Revenue and expenses'),
          description: this.$gettext('Revenue, expenses and margins of your company period time'),
          premium: false,
        },
        {
          section: 'free',
          to: { name: 'HoursReport' },
          icon: 'fa-clock',
          title: this.$gettext('Hours'),
          description: this.$gettext('Full report of all hours in your system'),
          premium: false,
        },
        {
          section: 'financial',
          to: { name: 'BalanceSheet' },
          icon: 'fa-euro',
          title: this.$gettext('Balance Sheet Investments'),
          description: this.$gettext(
            'Overview of all Balance Sheet costs and other assets and their write off periods'
          ),
          premium: true,
        },
        {
          section: 'financial',
          to: { name: 'FinancialStatements' },
          icon: 'fa-balance-scale',
          title: this.$gettext('Financial Statements'),
          description: this.$gettext(
            'Yearly financial Profit & Loss and Balance Sheet statements for tax and reporting purposes'
          ),
          premium: true,
        },
        {
          section: 'vat',
          to: { name: 'VATReportBEList' },
          icon: 'fa-database',
          title: this.$gettext('Belgian VAT (Beta)'),
          description: this.$gettext(
            "Automatic value-added tax report for the Belgian tax office based on your company's information"
          ),
          premium: true,
          condition: (c) => c.country === 'BE',
        },
        {
          section: 'vat',
          to: { name: 'TaxreportList' },
          icon: 'fa-database',
          title:
            this.currentCompany.country === 'BE' ? this.$gettext('VAT (Dutch Belastingdienst)') : this.$gettext('VAT'),
          description: this.$gettext('Automatic value added tax report based on your company data'),
          premium: true,
          condition: (c) => ['NL', 'BE'].includes(c.country),
        },
        {
          section: 'insight',
          to: { name: 'Products' },
          icon: 'fa-line-chart',
          title: this.$gettext('Products and services'),
          description: this.$gettext('Quantity and revenue of products and services you often invoice'),
          premium: true,
        },
        {
          section: 'insight',
          to: { name: 'CustomerInsight' },
          icon: 'fa-heart',
          title: this.$gettext('Customer insight'),
          description: this.$gettext('Trips, hours and profit & loss for each customer side-by-side'),
          premium: true,
        },
        {
          section: 'insight',
          to: { name: 'SupplierInsight' },
          icon: 'fa-cart-shopping',
          title: this.$gettext('Supplier insight'),
          description: this.$gettext('Expenses made per supplier'),
          premium: true,
        },
        {
          section: 'insight',
          to: { name: 'CostCategories' },
          icon: 'fa-pie-chart',
          title: this.$gettext('Cost categories'),
          description: this.$gettext('Expenses report by cost category'),
          premium: true,
        },
        {
          section: 'financial',
          to: { name: 'CashFlow' },
          icon: 'fa-area-chart',
          title: this.$gettext('Cash Flow'),
          description: this.$gettext('Explore the evolution of your cash flow'),
          premium: true,
        },
        {
          section: 'vat',
          to: { name: 'ForeignVATList' },
          icon: 'fa-globe-europe',
          title: this.$gettext('Foreign VAT'),
          description: this.$gettext('Tax report for foreign VAT charged via OSS'),
          premium: true,
          condition: (c) => c.country === 'NL',
        },
        {
          section: 'vat',
          to: { name: 'EUServicesList' },
          icon: 'fa-globe',
          title:
            this.currentCompany.country === 'BE'
              ? this.$gettext('Intracommunautaire Prestaties (Dutch Belastingdienst)')
              : this.$gettext('Intracommunautaire Prestaties'),
          description: this.$gettext('Belastingdienst tax report for exports within the EU with reverse charged VAT'),
          premium: true,
          condition: (c) => ['NL', 'BE'].includes(c.country),
        },
      ]

      this.visibleReports = allReports.filter((r) => !r.condition || r.condition(this.currentCompany))

      // Group by section
      this.sectionedReports = this.visibleReports.reduce((groups, report) => {
        if (!groups[report.section]) {
          groups[report.section] = []
        }
        groups[report.section].push(report)
        return groups
      }, {})
    },
  },
}
</script>

<style lang="scss" scoped>
.report-list {
  section {
    padding: 1rem 0;
    h3 {
      margin-bottom: 1rem;
      margin-top: 0.25rem;
    }
  }
  .report-cards-wrap {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    @media (max-width: 992px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }
  }
}
</style>